import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import CircularProgress from "@mui/material/CircularProgress";
import InputBase from "@mui/material/InputBase";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { SearchResult } from "../types/SearchResult";
import { ChapterVerseLocation } from "../types/ChapterVerseLocation";
import { VerseLocationForText } from "../types/VerseLocationForText";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { TransitionProps } from "@mui/material/transitions";
import { Box, Stack, lighten } from "@mui/material";
import { appBarColor } from "../constants/BaseConstants";

const defaultFontSize = 1.08; // in rem
const blueColorUrl = "#1565c0";
const resultsPerRequest = 20;
const azureSearchRootUrl = "https://quransearch.search.windows.net/indexes/blue-quran/docs";
const englishGodTranslationCode = "translationCode eq 'en-itani-v2-g'";
const englishAllahTranslationCode = "translationCode eq 'en-itani-v2-a'";

// Local helpers ----------------------------------------------------
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (/\bMac\b/.test(ua) && "ontouchend" in (window as any));
};

const stringAppend = ""; // keep non-fuzzy; set to "~1" to enable fuzzy

const appBar = { backgroundColor: appBarColor } as const;
const toolBar = { margin: 0, padding: 0, display: "flex", justifyContent: "center", alignItems: "center" } as const;
const searchOuterBox = {
  padding: "2px 10px",
  margin: "0px 10px",
  "@media(min-width: 375px)": { margin: "0px 20px" },
  backgroundColor: lighten(appBarColor, 0.8),
  borderRadius: "5px",
} as const;
const searchBox = {
  backgroundColor: lighten(appBarColor, 0.8),
  margin: 0,
  padding: 0,
  width: "210px",
  "@media(min-width: 350px)": { width: "250px" },
  "@media(min-width: 400px)": { width: "275px" },
  color: "#000",
} as const;

const dialogContent = { padding: 0, margin: 0 } as const;
const content = {
  maxWidth: "700px",
  paddingTop: "60px",
  display: "block",
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: "16px",
  paddingRight: "16px",
} as const;
const resultsTitle = { margin: "10px 0px 15px 5px" } as const;

const verse = {
  cursor: "pointer",
  lineHeight: "1.5",
  marginBottom: "0.9rem",
  color: "#000",
  boxShadow: "0 1px 4px 0 rgba(0,0,0,0.2)",
  borderRadius: "5px",
  padding: "15px",
} as const;
const wasNotClicked = { color: blueColorUrl } as const;
const wasClicked = { color: "#609" } as const;

// Left-aligned rows for spinner and "More" button (align with results left edge)
const loadMoreRow = {
  display: "block",
  marginTop: "15px",
  marginBottom: "60px",
  textAlign: "left",
} as const;

const spinnerStyle = {
  display: "inline-block",
  marginTop: "15px",
  marginBottom: "60px",
} as const;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<unknown, string> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSearchResultsCount(searchReply: any) {
  return searchReply["@odata.count"];
}

interface SearchProps {
  fontScaling: number;
  isOpen: boolean;
  searchVerseClick: (location: ChapterVerseLocation) => void;
  handleClose: () => void;
}

function Search(props: SearchProps): JSX.Element {
  const fontSize = React.useMemo(() => (props.fontScaling * defaultFontSize) / 100 + "rem", [props.fontScaling]);

  // State
  const [searchResults, setSearchResults] = React.useState<SearchResult[]>([]);
  const [isSpinnerVisible, setIsSpinnerVisible] = React.useState(false);
  const [isLoadMoreButtonVisible, setIsLoadMoreButtonVisible] = React.useState(false);
  const [numberOfResultsFound, setNumberOfResultsFound] = React.useState(-1);

  // Refs
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const resultsRef = React.useRef<SearchResult[]>([]); // source of truth for appending
  const skipRef = React.useRef(0);
  const ctrlRef = React.useRef<AbortController | null>(null);

  // Build query object fresh each request
  const makeQueryParams = React.useCallback((search: string, isAllah: boolean, skip: number) => {
    const params = new URLSearchParams();
    params.set("api-version", "2020-06-30");
    params.set("api-key", "50140970F6927FB81BFA79662E7F86FA");
    params.set("queryType", "full");
    params.set("searchMode", "all");
    params.set("$count", "true");
    params.set("$top", String(resultsPerRequest));
    params.set("highlightPreTag", "<b>");
    params.set("highlightPostTag", "</b>");
    params.set("$orderby", "verseIndex, weighting desc");
    params.set("searchFields", "text_en");
    params.set("highlight", "text_en");
    params.set("search", search);
    params.set("$filter", isAllah ? englishAllahTranslationCode : englishGodTranslationCode);
    params.set("$skip", String(skip));
    return params;
  }, []);

  // Parse response into SearchResult[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toSearchResults = React.useCallback((searchReply: any): SearchResult[] => {
    const arr: SearchResult[] = [];
    for (let i = 0; i < searchReply.value.length; i++) {
      const highlights: string[] = (searchReply.value[i]["@search.highlights"]?.["text_en"] ?? []) as string[];
      // collect highlighted tokens
      let allHighlightedWords: string[] = [];
      for (const snippet of highlights) {
        const hs = snippet.match(/<b>(.*?)<\/b>/g)?.map((val: string) => val.replace(/<\/?b>/g, "")) ?? [];
        allHighlightedWords = allHighlightedWords.concat(hs);
      }
      // unique
      allHighlightedWords = allHighlightedWords.filter((w, idx, self) => self.indexOf(w) === idx);

      let returnedText = searchReply.value[i]["text_en"] as string;
      for (const w of allHighlightedWords) {
        returnedText = returnedText.replace(new RegExp(w, "g"), `<span style="font-weight:bold">${w}</span>`);
      }
      arr.push(new SearchResult(searchReply.value[i].chapterNumber, searchReply.value[i].verseNumber, returnedText, false));
    }
    return arr;
  }, []);

  const appendToEveryWordInString = React.useCallback((inString: string, toAppend: string) => {
    return inString
      .trim()
      .split(/\s+/)
      .map((word) => `${word}${toAppend}`)
      .join(" ");
  }, []);

  // Core search using fetch + AbortController
  const searchAzure = React.useCallback(
    async (raw: string) => {
      const term = appendToEveryWordInString(raw.replace("!", ""), stringAppend);
      const isAllah = term.toLowerCase().includes("allah");

      // cancel any in-flight request
      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      setIsSpinnerVisible(true);
      setIsLoadMoreButtonVisible(false);

      const params = makeQueryParams(term, isAllah, skipRef.current);
      const url = `${azureSearchRootUrl}?${params.toString()}`;

      try {
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const searchReply = await res.json();

        if (searchReply.value.length > 0) {
          const next = resultsRef.current.concat(toSearchResults(searchReply));
          resultsRef.current = next;
          setSearchResults(next);
        }

        const total = getSearchResultsCount(searchReply);
        setNumberOfResultsFound(total);
        setIsSpinnerVisible(false);
        setIsLoadMoreButtonVisible(resultsRef.current.length < total);
      } catch (err: any) {
        if (err?.name === "AbortError") return; // expected on cancel
        setIsSpinnerVisible(false);
        setIsLoadMoreButtonVisible(false);
      }
    },
    [appendToEveryWordInString, makeQueryParams, toSearchResults],
  );

  // Submit handler (Enter key)
  const onFormSubmit = React.useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    inputRef.current?.blur();

    setNumberOfResultsFound(-1);
    setSearchResults([]);
    resultsRef.current = [];
    skipRef.current = 0;

    const value = (inputRef.current?.value ?? "").toString();
    if (value.trim().length === 0) return;
    searchAzure(value);
  }, [searchAzure]);

  // Load more keeps the same query string
  const onLoadMore = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const value = (inputRef.current?.value ?? "").toString();
    if (value.trim().length === 0) return;
    skipRef.current = resultsRef.current.length;
    searchAzure(value);
  }, [searchAzure]);

  // Click result -> navigate & mark clicked
  const onResultClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (window.getSelection?.().toString().length) return;
    const el = event.currentTarget as HTMLElement;
    const ch = Number(el.dataset.ch);
    const v = Number(el.dataset.v);
    const idx = Number(el.dataset.idx);
    if (Number.isNaN(ch) || Number.isNaN(v) || Number.isNaN(idx)) return;

    const gotoLocation = new ChapterVerseLocation(ch, new VerseLocationForText(v, true, true, 0));
    setSearchResults((prev) => {
      const next = prev.slice();
      if (next[idx]) next[idx].wasClicked = true;
      return next;
    });
    props.searchVerseClick(gotoLocation);
  }, [props.searchVerseClick]);

  // Focus behavior when the dialog opens
  React.useEffect(() => {
    if (!props.isOpen) {
      ctrlRef.current?.abort();
      return;
    }
    if (resultsRef.current.length === 0 && !isIOS()) inputRef.current?.focus();

    const el = inputRef.current;
    if (!el) return;
    const onMouseOver = () => el?.focus();
    el.addEventListener("mouseover", onMouseOver);
    return () => el.removeEventListener("mouseover", onMouseOver);
  }, [props.isOpen]);

  return (
    <Dialog keepMounted fullScreen open={props.isOpen} onClose={props.handleClose} TransitionComponent={Transition}>
      <DialogTitle>
        <AppBar sx={appBar}>
          <Toolbar sx={toolBar}>
            <IconButton edge="start" color="inherit" aria-label="Back" onClick={props.handleClose} size="large">
              <ArrowBack />
            </IconButton>
            <Stack direction="row" alignItems="center" sx={searchOuterBox}>
              <form noValidate autoComplete="off" onSubmit={onFormSubmit} action=".">
                {/* Controlled via a ref (no direct document.query) */}
                <InputBase placeholder="Search Quran…" id="input-base-search" inputRef={inputRef} sx={searchBox} type="search" />
              </form>
            </Stack>
          </Toolbar>
        </AppBar>
      </DialogTitle>

      <DialogContent sx={dialogContent}>
        <Box sx={content}>
          {numberOfResultsFound === 0 && <Box sx={resultsTitle}>No results found</Box>}
          {numberOfResultsFound === 1 && <Box sx={resultsTitle}>1 result found</Box>}
          {numberOfResultsFound > 1 && <Box sx={resultsTitle}>{numberOfResultsFound} results found</Box>}

          {searchResults.map((item, index) => (
            <Box
              sx={verse}
              style={{ fontSize }}
              key={`search-result-${item.chapterNumber}-${item.verseNumber}`}
              data-ch={item.chapterNumber}
              data-v={item.verseNumber}
              data-idx={index}
              onClick={onResultClick}
            >
              <Box component="span" sx={item.wasClicked ? wasClicked : wasNotClicked}>
                [{item.chapterNumber}:{item.verseNumber}]
              </Box>
              &nbsp;&nbsp;
              <Box component="span" contentEditable={false} dangerouslySetInnerHTML={{ __html: item.text }} />
            </Box>
          ))}

          {isSpinnerVisible && (
            <Box sx={loadMoreRow}>
              <CircularProgress size={30} sx={spinnerStyle} />
            </Box>
          )}

          {isLoadMoreButtonVisible && (
            <Box sx={loadMoreRow}>
              <Button variant="contained" onClick={onLoadMore}>
                More
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default React.memo(Search);
